import * as React from 'react';

import styles from './typography.module.scss';

type FontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;

type TypographyProps = React.HTMLAttributes<HTMLElement> & {
	weight?: FontWeight;
	italic?: boolean;
	underline?: boolean;
	transform?: 'uppercase' | 'lowercase' | 'capitalize' | 'none';
	align?: 'left' | 'center' | 'right' | 'justify';
	className?: string;
};

type TextProps = TypographyProps & {
	variant?: 'abstract' | 'quote' | 'body' | 'muted' | 'label' | 'success' | 'error';
};

type LinkProps = TypographyProps &
	React.AnchorHTMLAttributes<HTMLAnchorElement> & {
		external?: boolean;
	};

const createTypographyComponent = <T extends HTMLElement>(
	Component: keyof React.JSX.IntrinsicElements,
	baseClassName: string,
) => {
	const TypographyComponent = React.forwardRef<T, TypographyProps>(
		({ weight, italic, underline, transform, align, className, style, ...props }, ref) => {
			const combinedStyle = {
				fontWeight: weight,
				fontStyle: italic ? 'italic' : undefined,
				textDecoration: underline ? 'underline' : undefined,
				textTransform: transform,
				textAlign: align,
				...style,
			};

			return React.createElement(Component as string, {
				ref,
				className: `${styles[baseClassName]} ${className || ''}`,
				style: combinedStyle,
				...props,
			});
		},
	);

	TypographyComponent.displayName = Component as string;
	return TypographyComponent;
};

const H1 = createTypographyComponent<HTMLHeadingElement>('h1', 'h1');
const H2 = createTypographyComponent<HTMLHeadingElement>('h2', 'h2');
const H3 = createTypographyComponent<HTMLHeadingElement>('h3', 'h3');
const H4 = createTypographyComponent<HTMLHeadingElement>('h4', 'h4');
const H5 = createTypographyComponent<HTMLHeadingElement>('h5', 'h5');
const H6 = createTypographyComponent<HTMLHeadingElement>('h6', 'h6');

const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
	({ variant = 'body', className, ...props }, ref) => {
		const variantClassMap = {
			abstract: styles.abstract,
			quote: styles.quote,
			body: styles.body,
			muted: styles.muted,
			label: styles.label,
			success: styles.success,
			error: styles.error,
		};

		return <p ref={ref} className={`${variantClassMap[variant]} ${className || ''}`} {...props} />;
	},
);
Text.displayName = 'Text';

type CaptionProps = TypographyProps & {
	variant?: 'default' | 'muted';
};

const Caption = React.forwardRef<HTMLParagraphElement, CaptionProps>(
	({ variant = 'default', className, ...props }, ref) => {
		const variantClassMap = {
			default: styles.caption,
			muted: styles.captionMuted,
		};

		return <p ref={ref} className={`${variantClassMap[variant]} ${className || ''}`} {...props} />;
	},
);
Caption.displayName = 'Caption';

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
	({ external, className, ...props }, ref) => {
		const linkProps = external
			? {
					target: '_blank',
					rel: 'noopener noreferrer',
				}
			: {};

		return (
			<a ref={ref} className={`${styles.link} ${className || ''}`} {...linkProps} {...props} />
		);
	},
);
Link.displayName = 'Link';

export { H1, H2, H3, H4, H5, H6, Text, Link, Caption };
